import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client.js';
import { ApiError } from '../../api/client.js';
import type {
  DeliveryScopeType,
  ExportDataset,
  ExportSizeData,
  Grain,
  JobsExportEvent,
  PeriodKey,
  Targeting,
} from '../../api/types.js';
import { color, elevation, font, fontSize, radius } from '../../theme/tokens.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import type { ChartRowKey } from '../LineChart/chartData.js';

/**
 * Export this region's raw jobs to a Google Sheet (owner request 2026-09-12, FR-088).
 *
 * ## Why it is in the card HEADER and not a column in the matrix
 *
 * The obvious home is an actions column at the end of `<ChartTable>`'s rows, and it does not
 * fit. The matrix is `100 + 12×74 + 88 = 1,076px` against ~1,082px of card interior at a 1,280
 * viewport, a floor `tests/linechart.tableview.test.tsx` asserts and a 2026-09-10 owner decision
 * put there — "6px on any of them restores the scrollbar" is the note in `ChartTable.tsx`, and
 * even a single 40px icon column takes the floor to 1,116px and brings the sideways scroll back.
 *
 * So it sits in the header row instead, which turns out to be the better place anyway for three
 * reasons that are not consolations. The header is shared by **both** views, so the control is
 * reachable from the plot as well as the matrix. It is beside the region pills and the grain
 * control, which are the two things that decide what gets exported — a control has to be visible
 * next to what governs it (the same argument PRD-Delivery §11.6 makes for the lens). And it is
 * one control rather than one per row, which is what it actually is: there is one jobs export.
 *
 * ## What the button has to say before it is pressed
 *
 * Both of these are the subject of FR-034 — a figure or an action whose scope is not on its face.
 *
 * - **It needs exactly one region.** The Overview charts all seven by default, and "export all
 *   regions" is a 400,000-row sheet nobody wants and Sheets would struggle to write. So the
 *   control is disabled until one pill is selected and the panel says so, rather than exporting
 *   something enormous and calling it success.
 * - **It names the window on its panel.** The export covers the chart's twelve buckets, so the
 *   grain control silently changes the size of the file by two orders of magnitude — twelve days
 *   against twelve quarters. A reader who pressed the same button twice and got a 200-row sheet
 *   and a 50,000-row sheet, with nothing on screen explaining why, would reasonably think it
 *   broken.
 *
 * ## Where else it appears
 *
 * One component, six homes: the chart card's header on both region pages; the Region Performance
 * toolbar and each of its rows (FR-090); each **By Client** breakdown row (FR-091) and that
 * card's own toolbar, which exports every account two tabs each (FR-092); and Delivery's two
 * tables (FR-D27). What changes between them is the scope and the window —
 * `regionCode`/`allRegions`/`client`/`allClients`/`delivery`, and `grain` or `period` from
 * whichever card's control governs the surface — and nothing else, because the consent round
 * trip, the stream, the ring and the completion dialog have to behave identically wherever the
 * export is launched from. Two copies of this is how one of them comes to lose the
 * consent-resume fix.
 */

/**
 * The one region to export, or `null` when the chart is not showing exactly one.
 *
 * ⚠ **This exists because the inline version of it shipped broken and typechecked.** The Overview
 * holds the chart's selection twice: `chartRegions`, the `readonly string[]` the pills write, and
 * `chartScope`, the **string** request spelling (`'ALL'`, `'R3'`, `'R3,R4'`). The first cut read
 * `chartScope.length === 1`, which asks how many *characters* the scope has — `2` for `'R3'` —
 * so the button was disabled no matter what was selected. Nothing caught it: a string has
 * `.length` and indexes to `string`, so both halves compiled and both unit tests passed, because
 * they handed the component a `regionCode` directly and never exercised the derivation.
 *
 * Taking `readonly string[]` is the whole point of the function. A `string` is not assignable to
 * it — no `map`, no `filter` — so passing the scope here is a **compile error** rather than a
 * control that silently never enables.
 */
export function soleRegion(chartRegions: readonly string[]): string | null {
  return chartRegions.length === 1 ? (chartRegions[0] ?? null) : null;
}

/**
 * What window an export is cut by — the control it was launched from.
 *
 * ⚠ The chart's grain and the table's period are different controls on different cards, and the
 * server refuses both at once. Modelling it as a union rather than two optional fields is what
 * makes "neither" and "both" unrepresentable here instead of a 422 from the server.
 */
export type ExportWindow = { grain: Grain } | { period: PeriodKey };

interface RegionExportProps {
  /**
   * The region to export, or `null` when the chart is showing more than one.
   *
   * ⚠ **`null` means the control is unusable, not hidden.** A disabled control that says why is
   * how a reader learns the region pill is the thing to touch; a control that vanishes leaves
   * them looking for a feature they were told exists.
   */
  regionCode: string | null;
  /** The window, from whichever control governs this surface. */
  window: ExportWindow;
  /**
   * `true` when `regionCode: null` means **every region** rather than "no region chosen yet".
   *
   * ⚠ The two are opposite states wearing the same value. On the chart, `null` means the reader
   * has not narrowed to one region and the control must be **disabled**; on the Region
   * Performance toolbar it means the export covers all seven and the control must be **ready**.
   * Inferring one from the other would have made the table's button permanently unusable.
   */
  allRegions?: boolean;
  /**
   * Narrows the export to one account inside `regionCode` — the By Client breakdown's own
   * control (FR-091).
   *
   * ⚠ **Both halves are needed and they are not the same string.** `clientKey` is the
   * parent-preferred normalised identity the server files rows under; `clientName` is the raw
   * spelling the table shows, and it is used for the label alone. Sending the name would match
   * nothing, and showing the key would put `hca healthcare inc` on a control beside a row that
   * says `HCA Healthcare, Inc.`.
   *
   * ⚠ It is **meaningless without `regionCode`** — a breakdown row is a `(region, account)` pair
   * — and the server refuses the combination rather than widening it.
   */
  client?: { clientKey: string; clientName: string } | null;
  /**
   * Which tabs the file has — the chart matrix's action column, where the ROW is the dataset
   * (FR-093, owner request 2026-09-14).
   *
   * ⚠ **Omitted means both, and that is what every other surface wants.** The chart-card header
   * button, Region Performance's row and toolbar controls and the By Client exports are launched
   * from something that is not a single dataset, so narrowing them would be a guess. Only a
   * control sitting *in* the JOBS or SUBS row of the matrix knows which one the reader means.
   *
   * ⚠ It changes the **accessible name and the panel**, not just the request. A control whose
   * file holds one tab and whose label promises "jobs and submissions" is the FR-034 complaint
   * with a spreadsheet on the end of it.
   */
  dataset?: ExportDataset;
}

interface DeliveryExportProps {
  /**
   * The Delivery scope this file covers — a manager, a recruiter, a leader, or the whole table
   * (FR-D27, owner request 2026-09-12).
   *
   * ⚠ **`name` is carried beside the key and is not derived from it.** A manager's key is
   * `IMMEDIATE_MANAGER_ID` and a leader's is an upper-cased name; neither is what belongs on a
   * control or in a spoken label, and the page already has the real name in the row it is
   * rendering. The server gets the key and looks the name up again for the file's own face —
   * two readings of one identity, because a label posted from the browser is presentational
   * state that can go stale against the snapshot (the objection PRD §9.9.6 records).
   */
  delivery: { scopeType: DeliveryScopeType; scopeKey: string; name: string };
  /**
   * ⚠ **Always a period, never a grain**, and the type says so rather than leaving it to a 422.
   *
   * Both Delivery export controls sit on the Managers or Recruiters table, whose activity
   * columns are cut by the Timeline control (FR-D12) — so the file is cut by the same thing the
   * Subs cell it was launched from is. The chart's grain governs no figure in that table.
   */
  window: { period: PeriodKey };
}

/**
 * One control, two axes — and **never both at once**.
 *
 * The `never`s are what make that a compile error rather than a precedence rule nobody can see.
 * A component handed a region *and* a Delivery scope would have to pick one silently, which is
 * the shape of mistake this file already carries two scars from (`chartScope.length`, and
 * `regionCode: null` meaning two opposite things). Here it simply cannot be expressed.
 */
interface AccountsExportProps {
  /** Always a real region — "every account" is meaningless without one to enumerate. */
  regionCode: string;
  /**
   * Export **every account** in this region's By Client breakdown, two tabs each (FR-092).
   *
   * ⚠ **An object rather than a boolean, because the targeting travels with it.** On that card
   * `Target` lists the Primary accounts and `All` adds the Secondary ones, so the control decides
   * how many tabs the file has. A bare flag beside an orphan `targeting` prop would let a caller
   * set one and forget the other, and the file would cover a different set of accounts from the
   * table it was launched from — which is the FR-034 complaint, arriving inside a spreadsheet
   * where nothing on screen can correct it.
   */
  allClients: { targeting: Targeting };
  /**
   * ⚠ **Always a period, never a grain**, for the reason Delivery's is: the By Client card has a
   * Period control and no grain control, so a grain would cut the file by a window nothing on
   * screen names. The server refuses one too.
   */
  window: { period: PeriodKey };
}

/**
 * One control, three axes — and **never two at once**.
 *
 * The `never`s are what make that a compile error rather than a precedence rule nobody can see.
 * A component handed a region *and* a Delivery scope, or one account *and* every account, would
 * have to pick silently — the shape of mistake this file already carries two scars from
 * (`chartScope.length`, and `regionCode: null` meaning two opposite things). Here it cannot be
 * expressed.
 */
export type ExportButtonProps =
  | (RegionExportProps & { delivery?: undefined; allClients?: undefined })
  | (AccountsExportProps & {
      delivery?: undefined;
      allRegions?: undefined;
      client?: undefined;
      /**
       * ⚠ Excluded rather than ignored: this file is two tabs **per account**, so "which tabs"
       * has no answer here — a jobs-only spelling would have to mean 48 Jobs tabs, which is a
       * different feature nobody has asked for.
       */
      dataset?: undefined;
    })
  | (DeliveryExportProps & {
      regionCode?: undefined;
      allRegions?: undefined;
      client?: undefined;
      allClients?: undefined;
      /** ⚠ Excluded: a Delivery file is submissions by construction (FR-D09), not by choice. */
      dataset?: undefined;
    });

/**
 * The three query parameters that carry an export **across the Google consent round trip**.
 *
 * ⚠ **They exist because consent is a full page navigation, and the page does not survive it.**
 * Pressing Export with no grant sends the browser to Google; Google sends it back to a freshly
 * mounted SPA. Every piece of React state is gone — which region was selected, that an export was
 * ever wanted, that anything happened at all. The first cut set `?export=consent_granted` on the
 * way back and **nothing read it**, so a user completed consent successfully and landed on a page
 * that looked exactly as it had before: no sheet, no message, and the region pill cleared, so the
 * button was disabled again. That reads as "the feature is broken", and it is indistinguishable
 * from it.
 *
 * So the intent travels in the address, which is the only thing that does survive: the region and
 * grain go out with `returnTo`, come back beside the outcome, and are consumed once.
 */
const OUTCOME_PARAM = 'export';
export const PENDING_REGION_PARAM = 'exportRegion';
const PENDING_GRAIN_PARAM = 'exportGrain';
/**
 * ⚠ The account, for a by-client export that had to go through consent (FR-091).
 *
 * Without it a resumed client export silently widens to the whole region — a bigger sheet than
 * was asked for, landing with no sign that the scope was dropped, which is worse than the
 * failure it replaces.
 */
const PENDING_CLIENT_PARAM = 'exportClient';
/**
 * ⚠ The Delivery scope, for an org-axis export that had to go through consent (FR-D27).
 *
 * `${scopeType}:${scopeKey}` in one parameter rather than two, for the reason
 * `region_client_timeseries` refuses half a scope: the pair is meaningless apart, so one
 * parameter cannot arrive with the other missing. Its presence is also what *selects* the axis
 * on resume — a returning address carries either this or `exportRegion`, never both, because the
 * control that wrote it could only have been one of the two.
 */
const PENDING_DELIVERY_PARAM = 'exportScope';
/**
 * ⚠ The per-client export's **targeting**, across consent (FR-092).
 *
 * Deliberately not spelled `exportClients` — one character from `exportClient` and carrying a
 * completely different kind of value: a control setting, not an account key. Its presence is also
 * what selects this axis on resume, exactly as `exportScope` selects Delivery's.
 */
const PENDING_ACCOUNTS_PARAM = 'exportAccounts';

/**
 * Which tabs a resumed export writes (FR-093).
 *
 * ⚠ **It has to ride the address like the region and the window do.** Consent is a full page
 * navigation: the SPA remounts with no React state, so a reader who pressed the SUBS row's
 * control, granted Drive access and came back would otherwise resume the **two-tab** export — a
 * file with a Jobs tab they never asked for, landing silently. Absent means `both`, which is what
 * every control that does not set it wants anyway.
 */
const PENDING_DATASET_PARAM = 'exportDataset';

const GRAINS: readonly Grain[] = ['daily', 'weekly', 'monthly', 'quarterly'];
const PERIODS: readonly PeriodKey[] = ['MTD', '30d', '90d', '6M', '12M', '24M', 'YTD'];

/** The token that stands for "every region" across the consent round trip. */
const ALL_REGIONS_TOKEN = 'all';

/**
 * Rebuilds the window union from the one parameter that carried it.
 *
 * One parameter for both because a grain and a period can never collide — the four grain names
 * and the seven period codes share no spelling, and the server refuses both at once anyway.
 */
function asExportWindow(value: string | null): ExportWindow | null {
  if (value == null) return null;
  if ((GRAINS as readonly string[]).includes(value)) return { grain: value as Grain };
  if ((PERIODS as readonly string[]).includes(value)) return { period: value as PeriodKey };
  return null;
}

const GRAIN_WINDOW: Record<Grain, string> = {
  daily: 'the last 12 days',
  weekly: 'the last 12 weeks',
  monthly: 'the last 12 months',
  quarterly: 'the last 12 quarters',
};

/**
 * How the window reads on the control's own label.
 *
 * ⚠ Each control's **own vocabulary**: the chart says "the last 12 months" because that is what
 * twelve monthly buckets are, and the table says `30d` because that is the code on the segmented
 * control the reader just pressed. Translating either into the other's words would describe the
 * window correctly and still leave the reader unable to tie it back to what they touched.
 */
function windowLabel(w: ExportWindow): string {
  return 'grain' in w ? GRAIN_WINDOW[w.grain] : w.period;
}

/**
 * What the export is called on the button, which depends on how much of the book it covers.
 *
 * ⚠ A client export names **the account and its region**, never the account alone. The same
 * account has a row in more than one region's breakdown and those are different exports; a
 * control that said only "HCA Healthcare" would be the same words on two buttons producing two
 * different files — the same reason the sheet's own title carries both.
 */
function scopeLabel(
  regionCode: string | null,
  allRegions: boolean,
  client: { clientName: string } | null,
  allClients: boolean,
): string {
  // ⚠ "every R3 account", not "R3". The per-client file and the region file are different shapes
  // and both controls sit on the same page; a label naming only the region would make the two
  // indistinguishable to anyone reading the panel before pressing.
  if (allClients && regionCode) return `every ${regionCode} account`;
  if (client && regionCode) return `${client.clientName} (${regionCode})`;
  return regionCode ?? (allRegions ? 'all regions' : '');
}

/**
 * ⚠ Written here rather than served, unlike the too-large sentence.
 *
 * That one names figures the server measured and must match what the write path refuses with.
 * This one states a fact already on screen — the table right below says "No target accounts for
 * this region and period" — so the control only has to connect the disabled button to it.
 */
const EMPTY_SCOPE_REASON =
  'This region has no target accounts listed, so there is nothing to export. Switching ' +
  'Targeting to All may list some.';

type ExportState =
  | { phase: 'idle' }
  /** `total` is 0 until the server names it, which is one event before the first row lands. */
  | { phase: 'working'; written: number; total: number }
  | { phase: 'done'; url: string; rows: number }
  | { phase: 'failed'; message: string };

/**
 * The glyph on the export control — **a spreadsheet**, and it has two jobs at 14px.
 *
 * ## What it replaced, and why that one failed
 *
 * It was a page outline with two short horizontal rules inside. Those rules read as lines of
 * *text*, so the glyph said "document" where the whole point of this control is that it makes a
 * **sheet**. (Its comment claimed "a sheet with a row of cells, then an arrow leaving it" — there
 * was no arrow and no cells; the drawing and the comment had drifted apart.)
 *
 * ## ⚠ It must not become the view toggle's table icon
 *
 * `ViewToggle.tsx` already draws a table — rounded rect, heavy header rule, 3 columns — and on a
 * chart card the two controls sit **in the same row, inches apart**. A plain grid here would put
 * two near-identical glyphs on two controls that do completely different things, which is worse
 * than the document it replaced. Three things keep them apart, and all three are deliberate:
 *
 * - the header is a **solid band**, not a rule, so the silhouette differs at a glance;
 * - **two body columns**, against the toggle's three;
 * - the toggle is 16px in a segmented track, this is 14px in its own chip.
 *
 * Adding an export arrow was tried and rejected: at 14px the arrow needs enough length to read as
 * an arrow, which leaves the grid too small to read as a grid — so the glyph stopped saying
 * "sheet" at all, which is the thing being fixed.
 *
 * ⚠ **Butt caps on the two inner rules, not the `round` used elsewhere in this file.** A round cap
 * adds half the stroke width at each end, which pushed the column divider up into the header band
 * and made it read as a `T`. Inner rules meet the border and must stop exactly there.
 *
 * `currentColor` throughout — including the band's `fill` — so the whole glyph follows the
 * button's ink in both palettes and dims with it when the control is unusable.
 */
function SheetIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      {/* The header row, filled — the one thing every spreadsheet has and no text document does. */}
      <path d="M4.7 6.4h14.6v2.7H4.7z" fill="currentColor" stroke="none" />
      {/* One body rule and one column divider: a 2×2 body, which stays legible at 14px. */}
      <path d="M3 14.2h18M12 10.6v8.9" />
    </svg>
  );
}

/**
 * Where Google should drop the browser after consent — this page, plus the export to resume.
 *
 * The server validates and re-bases this against `APP_ORIGIN`, so it is a hint rather than a
 * trust boundary; what matters here is that it carries enough to finish the job.
 */
/**
 * One export, fully described — everything `run` needs and everything that has to survive consent.
 *
 * ⚠ **A value, not the props.** A resumed export is the one the *address* describes, which by
 * then may not be the one this instance's props are for: the page has remounted with its
 * selection cleared, and whichever control mounts first is the one that finishes the job. Passing
 * the target in is what makes those two paths the same code.
 */
type ExportTarget =
  | {
      kind: 'region';
      region: string | null;
      window: ExportWindow;
      clientKey: string | null;
      /** Which tabs (FR-093) — carried on the target so a resumed export writes the same file. */
      dataset: ExportDataset;
    }
  /** Every account in one region, two tabs each (FR-092). */
  | {
      kind: 'accounts';
      region: string;
      window: { period: PeriodKey };
      targeting: Targeting;
    }
  | {
      kind: 'delivery';
      scopeType: DeliveryScopeType;
      scopeKey: string;
      window: { period: PeriodKey };
    };

function returnToFor(target: ExportTarget): string {
  const back = new URL(window.location.href);

  // One parameter for the window on both axes: the four grain names and the seven period codes
  // share no spelling, so `asExportWindow` can tell them apart on the way back.
  back.searchParams.set(
    PENDING_GRAIN_PARAM,
    'grain' in target.window ? target.window.grain : target.window.period,
  );

  if (target.kind === 'delivery') {
    back.searchParams.set(PENDING_DELIVERY_PARAM, `${target.scopeType}:${target.scopeKey}`);
    back.searchParams.delete(PENDING_ACCOUNTS_PARAM);
    // ⚠ The region parameters are *deleted*, not left alone. The address may already be carrying
    // them from an earlier region export on another page, and a resume that found both would
    // have to choose — which is the precedence this design refuses to have.
    back.searchParams.delete(PENDING_REGION_PARAM);
    back.searchParams.delete(PENDING_CLIENT_PARAM);
  } else {
    // ⚠ `all` rather than omitting the parameter: an absent region is indistinguishable from a
    // resume that lost it, and the resume would then do nothing at all.
    back.searchParams.set(PENDING_REGION_PARAM, target.region ?? ALL_REGIONS_TOKEN);

    if (target.kind === 'accounts') {
      // ⚠ The targeting, not just a marker: it decides which accounts are listed and so how many
      // tabs the file has. A resume that lost it would export a different set of accounts from
      // the one the reader was looking at when they pressed.
      back.searchParams.set(PENDING_ACCOUNTS_PARAM, target.targeting);
      back.searchParams.delete(PENDING_CLIENT_PARAM);
    } else {
      // Absent, not a token: there is no "all accounts" spelling — a region export simply has no
      // account, and an empty string here would be a key the server then refuses.
      if (target.clientKey) back.searchParams.set(PENDING_CLIENT_PARAM, target.clientKey);
      else back.searchParams.delete(PENDING_CLIENT_PARAM);
      back.searchParams.delete(PENDING_ACCOUNTS_PARAM);
      // ⚠ Deleted when it is `both`, never written as the word: the address may be carrying a
      // narrowing from an earlier export on this page, and a stale `exportDataset=subs` would
      // resume the header button's two-tab export as a one-tab one.
      if (target.dataset !== 'both') back.searchParams.set(PENDING_DATASET_PARAM, target.dataset);
      else back.searchParams.delete(PENDING_DATASET_PARAM);
    }
    back.searchParams.delete(PENDING_DELIVERY_PARAM);
  }

  // Path and query only — the server decides the origin.
  return `${back.pathname}${back.search}`;
}

/** Rebuilds a Delivery target from the one parameter that carried it across consent. */
function asDeliveryTarget(value: string | null, forWindow: ExportWindow): ExportTarget | null {
  if (!value) return null;
  const separator = value.indexOf(':');
  if (separator <= 0) return null;
  const scopeType = value.slice(0, separator);
  const scopeKey = value.slice(separator + 1);
  // ⚠ A leader key is a name and can contain anything but a newline, so the split is on the
  // FIRST colon only — `indexOf`, never `split(':')`, which would truncate one at a colon.
  if (!DELIVERY_SCOPE_TYPES.includes(scopeType as DeliveryScopeType) || !scopeKey) return null;
  // A grain here means the address was hand-edited: Delivery exports are cut by the period.
  if (!('period' in forWindow)) return null;
  return {
    kind: 'delivery',
    scopeType: scopeType as DeliveryScopeType,
    scopeKey,
    window: forWindow,
  };
}

const DELIVERY_SCOPE_TYPES: readonly DeliveryScopeType[] = [
  'all',
  'leader',
  'leaders',
  'manager',
  'recruiter',
];

/**
 * A ring that fills as rows land (owner request 2026-09-12).
 *
 * ⚠ **It is only ever drawn from a real figure.** A big export is ~74 seconds — thirteen Sheets
 * calls at ~5.7s each for R3's 63,344 monthly rows — and the server reports rows Google has
 * *accepted* after every chunk, so the sweep is the truth. A ring animated against a guessed
 * duration would look identical and mean nothing, which is worse than no ring at all: it invites
 * a reader to plan around a number the page invented.
 *
 * ⚠ **Before the server names a total it spins instead of sitting at 0%.** The first second or two
 * is the Mongo read, where the denominator genuinely is not known yet; a ring frozen at zero reads
 * as stuck, which is the state this whole control exists to remove.
 *
 * The geometry is `stroke-dasharray` on a circle with a `-90deg` rotation, so the sweep starts at
 * twelve o'clock. `strokeLinecap: round` keeps a 1% sweep visible — at `butt` the first chunk of a
 * thirteen-chunk export draws almost nothing and the ring looks broken on the longest exports.
 */
function ProgressRing({ percent }: { percent: number | null }) {
  const size = 15;
  const stroke = 2.5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const swept = percent == null ? circumference * 0.25 : circumference * (percent / 100);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
      style={{
        transform: 'rotate(-90deg)',
        // The indeterminate case spins; a determinate one must not, or the figure is unreadable.
        animation: percent == null ? 'export-ring-spin 900ms linear infinite' : undefined,
      }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color.borderChip}
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color.brandTo}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${swept} ${circumference}`}
        // Smooths the step between chunks — 5,000 rows arrive at once, so without this the ring
        // jumps in thirteen visible increments.
        style={{ transition: percent == null ? undefined : 'stroke-dasharray 300ms linear' }}
      />
    </svg>
  );
}

/**
 * The finished export, as a dialog (owner request 2026-09-12).
 *
 * The result used to be a link sitting beside the button. It is a dialog now for the reason the
 * export is worth reporting at all: it takes **~74 seconds** for a big region, by which time the
 * reader has looked away — and a line of 11px text appearing quietly in a control row is not
 * something you come back to and notice. This is the shape BigQuery uses after *Save results →
 * Google Sheets*, and for the same reason.
 *
 * ## ⚠ Opening the sheet is the reader's click, never ours
 *
 * `window.open` here would be a popup ~74 seconds after the gesture that caused it, which is
 * precisely what a popup blocker exists to stop — the sheet would silently not open. The primary
 * action is a real `<a target="_blank">`, so the navigation is user-initiated and always works.
 *
 * ## What it borrows, and why it borrows rather than invents
 *
 * Principle I: a surface absent from the blueprint is composed from blueprint primitives. The
 * scrim, the radius, the palette elevation and the click-outside-to-close are `<CommandPalette>`'s
 * — this product has one overlay idiom and this is it.
 */
function ExportDoneDialog({
  rows,
  url,
  region,
  tabs,
  onClose,
}: {
  rows: number;
  url: string;
  region: string | null;
  /**
   * ⚠ What the file actually contains, because the dialog names it and a Delivery export has no
   * Jobs tab (FR-D09). A reader told "Jobs and Submissions on separate tabs" who opens a file
   * with one tab has been given a reason to distrust the row count beside it.
   */
  /** What shape the file is, which decides how the dialog describes it. */
  tabs: 'jobs-and-submissions' | 'submissions' | 'per-account';
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    // Focus the primary action, so Enter opens the sheet and the dialog is usable without a
    // mouse — and so a screen reader lands inside the dialog rather than announcing it and
    // leaving the caret behind on the page.
    const id = window.setTimeout(() => closeRef.current?.focus(), 0);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(id);
    };
  }, [onClose]);

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: color.scrim,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Export complete"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(380px, 92vw)',
          background: color.surface,
          border: `1px solid ${color.borderStrong}`,
          borderRadius: radius['3xl'],
          boxShadow: elevation.palette,
          padding: '22px 24px 18px',
          fontFamily: font.sans,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: fontSize.body, fontWeight: 700, color: color.ink }}>
          Export complete
        </div>
        <div style={{ marginTop: 6, fontSize: fontSize.caption, color: color.inkMuted }}>
          {/* Says what landed, so the dialog is still meaningful to someone who has forgotten
              which control they pressed a minute and a half ago. */}
          {/* "rows" rather than "jobs": the file is two tabs, and naming one of them would
              quietly under-report what landed. */}
          {/*
            ⚠ The per-account file gets its own sentence rather than the scope spliced in front of
            "rows". `every R3 account` reads as a scope, not as an adjective, so the shared
            phrasing produced "52 every R3 account rows written" — and "on separate tabs" badly
            understates a file with two tabs per account. The dialog is what a reader comes back
            to ninety seconds later; it has to describe the object that actually landed.
          */}
          {tabs === 'per-account' ? (
            <>
              {rows.toLocaleString('en-US')} rows written to a new Google Sheet in your Drive —{' '}
              {region}, with Jobs and Subs tabs for each.
            </>
          ) : (
            <>
              {rows.toLocaleString('en-US')} {region ? `${region} ` : ''}rows written to a new
              Google Sheet in your Drive
              {tabs === 'submissions'
                ? ' — submissions, one row each.'
                : ' — Jobs and Submissions on separate tabs.'}
            </>
          )}
        </div>

        <div
          style={{
            marginTop: 18,
            display: 'flex',
            gap: 8,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <a
            ref={closeRef}
            href={url}
            target="_blank"
            rel="noreferrer"
            // Opening it is the whole purpose, so the dialog closes behind the click rather than
            // waiting to be dismissed a second time.
            onClick={onClose}
            style={{
              padding: '8px 16px',
              borderRadius: radius.lg,
              background: color.brandTo,
              color: color.onBrand,
              fontSize: fontSize.captionStrong,
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            Open sheet
          </a>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 14px',
              borderRadius: radius.lg,
              background: color.surfaceTrack,
              border: `1px solid ${color.borderChip}`,
              color: color.inkMuted,
              fontSize: fontSize.captionStrong,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Which chart matrix rows export, and what each one writes (FR-093, owner request 2026-09-14).
 *
 * ⚠ **Defined once, because two pages render this column** — the Overview's chart and Region
 * Detail's — and a row that exported on one page and not the other would read as a fault on
 * whichever page lost it. The same rule `store/queries.ts` follows for cache keys.
 *
 * ⚠ **The absent rows are absent for a reason, not from neglect.** `toa` is an as-of stock: there
 * is no such thing as "the records behind" a count of who is on assignment right now, and the
 * stored export collections hold jobs and submittals alone. `offers` has no export collection at
 * all. Delivery's four table-only rows (`backouts`, `starts`, `terms`, `uniqueSubs`) are the same
 * absence — and `uniqueSubs` is the trap of the four: `submission_export_rows` would happily
 * produce a file for it, one row per submission, while the cell counts distinct *candidates*
 * (FR-084). The row count would not match the figure it sits beside, which is the one promise
 * every export in this product makes.
 */
export const CHART_ROW_DATASET: Partial<Record<ChartRowKey, Exclude<ExportDataset, 'both'>>> = {
  jobs: 'jobs',
  subs: 'subs',
};

export function ExportButton(props: ExportButtonProps) {
  const { window: exportWindow } = props;
  const delivery = props.delivery ?? null;
  const regionCode = props.regionCode ?? null;
  const allRegions = props.allRegions ?? false;
  const client = props.client ?? null;
  const allClients = props.allClients ?? null;
  // ⚠ Only the region axis has a dataset. Delivery is submissions by construction (FR-D09) and
  // the per-account file is two tabs per account, so reading the prop off those unions would be
  // offering a narrowing neither route accepts.
  const dataset: ExportDataset = props.dataset ?? 'both';

  const [state, setState] = useState<ExportState>({ phase: 'idle' });

  /**
   * ⚠ **A Delivery scope is always usable**, where a region one may not be.
   *
   * The region control is disabled until the chart is narrowed to one region, because its default
   * is all seven and that export is enormous. Delivery has no such state: every scope this
   * control is rendered for is a row that exists or the table's own scope, and even the widest of
   * them (all managers at `24M`) is 4.5% of Google's ceiling. A Delivery button that could be
   * disabled would be disabled for a reason with nothing on screen to act on.
   */
  const hasScope = delivery != null || allRegions || regionCode != null;
  const working = state.phase === 'working';

  /** The export this instance's props describe — the one a click runs. */
  const target: ExportTarget = delivery
    ? {
        kind: 'delivery',
        scopeType: delivery.scopeType,
        scopeKey: delivery.scopeKey,
        window: exportWindow as { period: PeriodKey },
      }
    : allClients && regionCode
      ? {
          kind: 'accounts',
          region: regionCode,
          window: exportWindow as { period: PeriodKey },
          targeting: allClients.targeting,
        }
      : {
          kind: 'region',
          region: regionCode,
          window: exportWindow,
          clientKey: client?.clientKey ?? null,
          dataset,
        };

  /**
   * `null` while the denominator is unknown — the Mongo read, before the server names a total.
   *
   * ⚠ Floored at 1 once any row has landed: `Math.round` puts the first of thirteen chunks at 8%,
   * but a smaller first chunk would round to `0%` and read as "nothing has happened" beside a
   * ring that has visibly moved.
   */
  const percent =
    state.phase === 'working' && state.total > 0
      ? Math.max(1, Math.round((state.written / state.total) * 100))
      : null;

  /**
   * `region` and `grain` are passed in rather than read from props, because a **resume** after
   * consent has to run for the region the user picked *before* the navigation — which the props
   * no longer know, the page having remounted with its selection cleared.
   */
  async function run(forTarget: ExportTarget) {
    if (working) return;
    setState({ phase: 'working', written: 0, total: 0 });

    const onEvent = makeEventHandler();

    try {
      const returnTo = returnToFor(forTarget);

      await (forTarget.kind === 'delivery'
        ? api.exportDeliverySubmissions(
            { scopeType: forTarget.scopeType, scopeKey: forTarget.scopeKey },
            { period: forTarget.window.period, returnTo },
            onEvent,
          )
        : forTarget.kind === 'accounts'
        ? api.exportRegionClients(
            forTarget.region,
            { period: forTarget.window.period, targeting: forTarget.targeting, returnTo },
            onEvent,
          )
        : api.exportRegionJobs(
            forTarget.region,
            {
              ...forTarget.window,
              returnTo,
              ...(forTarget.clientKey ? { clientKey: forTarget.clientKey } : {}),
              // ⚠ Omitted when it is `both`, so every existing caller sends the request it always
              // sent and the server's default is the only place the meaning of "absent" lives.
              ...(forTarget.dataset !== 'both' ? { dataset: forTarget.dataset } : {}),
            },
            onEvent,
          ));
    } catch (error) {
      setState({
        phase: 'failed',
        message:
          error instanceof ApiError ? error.message : 'The export did not finish. Try again.',
      });
    }
  }

  /**
   * The one reader of the NDJSON stream, shared by both axes.
   *
   * ⚠ Shared deliberately: the phases, the consent navigation and the completion state have to
   * behave identically wherever the export was launched from, and a second copy of this switch is
   * how one axis comes to lose the `consent_required` branch while the other keeps it.
   */
  function makeEventHandler() {
    return (event: JobsExportEvent) => {
      switch (event.phase) {
        case 'reading':
          setState({ phase: 'working', written: 0, total: 0 });
          return;
        case 'writing':
          setState({ phase: 'working', written: event.rowsWritten, total: event.totalRows });
          return;
        case 'done':
          setState({ phase: 'done', url: event.spreadsheetUrl, rows: event.rowCount });
          return;
        case 'consent_required':
          /*
           * A full navigation, not a popup. The consent screen is Google's own page and a popup
           * blocker kills a `window.open` that happens after an await — which is exactly this
           * one. The server bounces the browser back to `returnTo` when done.
           */
          window.location.href = event.authUrl;
          return;
        case 'unavailable':
          setState({ phase: 'failed', message: event.reason });
          return;
        case 'failed':
          setState({ phase: 'failed', message: event.message });
          return;
      }
    };
  }

  /**
   * Pick the export back up after the consent round trip.
   *
   * Three things about this are deliberate.
   *
   * **The parameters are stripped before anything else happens**, so a refresh — or the browser
   * restoring the tab — cannot fire a second export, and the address stops carrying a one-shot
   * instruction the moment it is consumed.
   *
   * ⚠ **The ref guards React 18 StrictMode**, which mounts, unmounts and remounts every component
   * in development. Without it the effect runs twice and the second run reads an address the
   * first already cleaned — harmless today, but it is the same guard that stops a genuine double
   * export if the cleanup ever moves.
   *
   * ⚠ **`consent_failed` must say something.** Silence after a user has just been through a
   * Google consent screen is exactly the state this whole mechanism exists to remove.
   */
  /**
   * How big this export would be — asked whenever the scope or the window changes (FR-090a).
   *
   * ⚠ **`null` means "not asked yet", not "fine".** The control stays usable while the answer is
   * in flight (it arrives in well under a second, and the server guard is still the backstop), so
   * a reader is never blocked by a check they cannot see. Only a definite `withinLimit: false`
   * disables it.
   *
   * ⚠ **The reason comes from the server**, composed beside the figures it names. A sentence
   * written here would be a second wording of the same limit and would drift from what the write
   * path actually refuses with.
   */
  const [size, setSize] = useState<ExportSizeData | null>(null);
  const windowKey = 'grain' in exportWindow ? exportWindow.grain : exportWindow.period;

  /**
   * ⚠ **A by-client export does not ask** — and the reason is the page, not the figure.
   *
   * This control is rendered once per breakdown row, and a region's breakdown runs to a few
   * hundred accounts; asking on mount would be a few hundred requests to answer a question whose
   * answer cannot be no. The ceiling is 10 million cells, which at 26 columns is ~384,000 jobs —
   * more than the whole book across all seven regions, let alone one account inside one of them.
   *
   * The server still checks before it creates anything, so the worst case is the refusal this
   * pre-flight exists to replace, arriving after a click instead of before it.
   */
  /**
   * ⚠ **A Delivery export does not ask either**, and for a sturdier reason than the by-client one.
   *
   * That one skips the check because a few hundred controls on a page would be a few hundred
   * requests; this one skips it because the answer cannot be no. Measured 2026-09-12, the largest
   * Delivery export in existence — every manager at `24M` — is 22,707 rows over 20 columns, 4.5%
   * of Google's ten-million-cell ceiling, and the largest single manager is 2,967 rows. The
   * server still checks before it creates anything (`/api/delivery/export/size` serves the same
   * figure for anyone who wants to verify that), so the worst case stays a refusal that names its
   * numbers rather than a 400 from Google six minutes in.
   */
  /**
   * ⚠ **The per-client export DOES ask, unlike the two skips above**, and the difference is the
   * row count rather than the control count. There is one of these per page, not one per row, and
   * the file it produces holds a whole region's rows plus a header per tab — the same order of
   * magnitude as the region export, which has always asked. It also asks a **different route**:
   * `exportSize` answers for a region or one account and charges two headers, where this file
   * charges one per tab across up to 48 of them.
   */
  const checksSize = delivery == null && client == null;
  const accountsTargeting = allClients?.targeting ?? null;

  useEffect(() => {
    if (!hasScope || !checksSize) return;

    let cancelled = false;
    void (
      accountsTargeting && regionCode
        ? api.exportClientsSize(regionCode, {
            period: (exportWindow as { period: PeriodKey }).period,
            targeting: accountsTargeting,
          })
        : api.exportSize(regionCode, {
            ...exportWindow,
            // ⚠ Sent whenever it narrows, or the JOBS row's control pre-flights the two-tab
            // figure: at `24M` all-regions that is 10.60M cells and the button disables itself
            // over a Submissions tab its file would not contain.
            ...(dataset !== 'both' ? { dataset } : {}),
          })
    )
      .then((envelope) => {
        if (!cancelled) setSize(envelope.data);
      })
      .catch(() => {
        // ⚠ A failed check must not disable the control. The server still guards the write, so
        // the worst case is the refusal this feature replaces — never a button that cannot be
        // pressed because a secondary request went wrong.
        if (!cancelled) setSize(null);
      });

    return () => {
      cancelled = true;
    };
    /*
     * Keyed on the scope and the window — the only two things that move the figure.
     *
     * ⚠ **`hasScope`, never the derived `ready`.** `ready` now folds in `tooLarge`, which is the
     * answer this effect fetches: depending on it would make the effect re-run on its own result.
     */
    // ⚠ `accountsTargeting` is in the deps because it moves the figure: switching the card's
    // Targeting control changes which accounts are listed and therefore how many tabs and rows
    // the file has. Leaving it out would leave a stale size beside a changed scope.
    // ⚠ `dataset` moves the figure too — it decides which tabs are charged — so a control that
    // switched datasets without re-asking would carry the other row's measurement.
  }, [regionCode, windowKey, hasScope, checksSize, accountsTargeting, dataset]);


  const tooLarge = size != null && !size.withinLimit;

  /**
   * ⚠ **A region with no listed accounts has nothing to export**, and SPOT is that region today.
   *
   * The breakdown renders "No target accounts for this region and period"; pressing Export there
   * would create a spreadsheet in the reader's Drive containing nothing but the notes tab, which
   * is worse than a refusal — it looks like a successful export of an empty book rather than a
   * question that does not apply.
   *
   * ⚠ Deliberately **not** the same rule as FR-091's "an unknown account key exports an empty
   * sheet, not an error". That is an account with no *rows*, which is a real row in the table and
   * a real answer. This is no *accounts*: there is no file to make.
   *
   * It reads `clientCount` off the pre-flight, so it is only ever a definite answer — `null`
   * while the check is in flight leaves the control usable, exactly as `tooLarge` does.
   */
  const emptyScope = allClients != null && size != null && size.clientCount === 0;

  const resumed = useRef(false);

  useEffect(() => {
    if (resumed.current) return;

    const params = new URLSearchParams(window.location.search);
    const outcome = params.get(OUTCOME_PARAM);
    if (!outcome) return;

    resumed.current = true;

    const clean = new URL(window.location.href);
    for (const key of [
      OUTCOME_PARAM,
      PENDING_REGION_PARAM,
      PENDING_GRAIN_PARAM,
      PENDING_CLIENT_PARAM,
      PENDING_ACCOUNTS_PARAM,
      PENDING_DELIVERY_PARAM,
      PENDING_DATASET_PARAM,
    ]) {
      clean.searchParams.delete(key);
    }
    window.history.replaceState({}, '', `${clean.pathname}${clean.search}`);

    if (outcome === 'consent_failed') {
      setState({
        phase: 'failed',
        message: 'Google access was not granted. Press Export to try again.',
      });
      return;
    }

    const pendingRegion = params.get(PENDING_REGION_PARAM);
    const pendingWindow = asExportWindow(params.get(PENDING_GRAIN_PARAM));
    /*
     * ⚠ **The resumed export is the one the address describes, not the one this instance is
     * for.** A page can carry many of these controls — the chart's, and one per breakdown row —
     * and whichever mounts first strips the parameters, so the others see a clean address and
     * stand down. That means the control that finishes the job is usually not the one that
     * started it, which is fine precisely because every part of the scope travels in the
     * address: region, window and account. Reading the account from `client` here instead would
     * resume a row's export as the chart's, silently widening it to the whole region.
     */
    const pendingClient = params.get(PENDING_CLIENT_PARAM);
    if (outcome !== 'consent_granted' || !pendingWindow) return;

    /*
     * ⚠ **The axis comes from the address, not from this instance's props.** A Delivery scope and
     * a region are mutually exclusive here for the same reason they are in the props: the control
     * that started this could only have been one of the two, so the returning address carries one
     * parameter or the other. Preferring the Delivery one is not a precedence — `returnToFor`
     * deletes whichever set it did not write, so they are never both present.
     */
    const pendingDelivery = asDeliveryTarget(params.get(PENDING_DELIVERY_PARAM), pendingWindow);
    if (pendingDelivery) {
      void run(pendingDelivery);
      return;
    }

    /*
     * ⚠ Checked before the plain region branch, because a per-client resume carries a region
     * too — it is "every account *in R3*". Reading `exportRegion` first would resume it as the
     * region export: one file of two tabs instead of one file of forty-eight, landing silently.
     */
    const pendingAccounts = params.get(PENDING_ACCOUNTS_PARAM);
    if (
      pendingRegion &&
      pendingRegion !== ALL_REGIONS_TOKEN &&
      (pendingAccounts === 'target' || pendingAccounts === 'all') &&
      'period' in pendingWindow
    ) {
      void run({
        kind: 'accounts',
        region: pendingRegion,
        window: pendingWindow,
        targeting: pendingAccounts,
      });
      return;
    }

    if (pendingRegion) {
      const pendingDataset = params.get(PENDING_DATASET_PARAM);
      void run({
        kind: 'region',
        region: pendingRegion === ALL_REGIONS_TOKEN ? null : pendingRegion,
        window: pendingWindow,
        clientKey: pendingClient,
        // ⚠ Anything but the two narrowing spellings resumes as `both` — the server's own
        // default. A tampered or truncated parameter must not make the export refuse; it is a
        // resume of work the reader already asked for, and the widest reading is the safe one
        // here precisely because `both` is what every unnarrowed control sends.
        dataset: pendingDataset === 'jobs' || pendingDataset === 'subs' ? pendingDataset : 'both',
      });
    }
    // Once, on mount. The address is read here and nowhere else, and `run` is deliberately not a
    // dependency: re-running this on a prop change would re-read parameters already consumed.
  }, []);

  /**
   * ⚠ **With no label on the face, this IS the label** (owner request 2026-09-12).
   *
   * The control was `⬓ Export jobs` and is now the glyph alone, so everything a reader needs —
   * what it does, which region, which window, and how far along it is — has to live in the
   * disclosure and the accessible name. That is FR-062's whole subject: an icon-only trigger with
   * no name serves nobody, and `title` alone serves a pointer and leaves the keyboard out.
   *
   * ⚠ The **progress** belongs here too, now that `Exporting… 62%` is gone from the face. A ring
   * sweeping with no figure anywhere reachable is a worse answer than the text it replaced.
   */
  const scope = delivery
    ? delivery.name
    : scopeLabel(regionCode, allRegions, client, allClients != null);
  /**
   * What the file holds, in the words of the page the control is on.
   *
   * ⚠ **`submissions` alone on Delivery is a statement, not a shorter label.** A job has no
   * recruiter, so there is no Jobs tab and there cannot be one (FR-D09) — the same absence that
   * keeps a JOBS line off this page's chart and a Jobs column off both its tables. Saying "jobs
   * and submissions" here would promise half a file that is never coming.
   */
  /*
   * ⚠ **The narrowed control must say what it writes** (FR-093). It is the only thing separating
   * the JOBS row's button from the SUBS row's — they sit one row apart in the same column, on the
   * same card, over the same window, and the glyph is identical. Without this the two are
   * indistinguishable to a screen reader and to a pointer alike, which is the failure the action
   * column was added to fix.
   */
  const contents = delivery
    ? 'submissions'
    : dataset === 'jobs'
      ? 'jobs'
      : dataset === 'subs'
        ? 'submissions'
        : 'jobs and submissions';
  // ⚠ The per-client file says **two tabs per account**, because that is the thing a reader is
  // most likely to be surprised by — a 48-tab spreadsheet is a different object from a two-tab
  // one, and the panel is the only place that can say so before it exists.
  const tabs =
    delivery || dataset !== 'both'
      ? 'one tab'
      : allClients
        ? 'two tabs per account'
        : 'one tab each';
  // Declared after `tooLarge` is known, so the control reflects both reasons it can be unusable.
  const ready = hasScope && !tooLarge && !emptyScope;

  const panel = working
    ? percent == null
      ? 'Exporting… reading the rows'
      : `Exporting ${scope} ${contents} · ${percent}% written`
    : tooLarge
      ? // ⚠ The server's own sentence, naming the figures and the two ways out. This is the whole
        // point of the check: a control that cannot be pressed has to say why, and "too large"
        // without a number or a remedy is worse than letting the click fail.
        (size?.reason ?? 'This export is too large for one Google Sheet.')
      : emptyScope
        ? EMPTY_SCOPE_REASON
        : !hasScope
        ? 'Export jobs and submissions · pick one region first — the chart is showing several'
        : `Export ${scope} ${contents} to a Google Sheet · ${windowLabel(exportWindow)}, ${tabs}`;

  return (
    /*
     * ⚠ **`position: relative` is what keeps the live region below inside the table.** `.sr-only`
     * is `position: absolute`, and with no positioned ancestor its containing block is the PAGE,
     * not `<DataTable>`'s scroller — which is `position: static`, so it clips only descendants it
     * contains. Every action-column row's empty status span then sat at its unclipped static
     * position — the far right of a 2,174px table, and rows below the body's height cap — and
     * stretched the document both ways: an extra horizontal and vertical page scroll on Delivery
     * and both Region pages. Both overlays here are `position: fixed`, which this does not move.
     */
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}>
      <Disclosure
        accessibleName={
          working
            ? percent == null
              ? `Exporting ${contents}, reading the rows`
              : `Exporting ${scope} ${contents}, ${percent}% complete`
            : tooLarge
              ? `Export unavailable — ${size?.reason ?? 'this export is too large for one Google Sheet.'}`
              : emptyScope
                ? `Export unavailable — ${EMPTY_SCOPE_REASON}`
              : ready
                ? `Export ${scope} ${contents} to a Google Sheet, covering ${windowLabel(exportWindow)}`
                : `Export ${contents} — select a single region first`
        }
        panel={panel}
        onClick={() => ready && void run(target)}
        disabled={!ready || working}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Square, because there is no label to set the width any more (owner request
          // 2026-09-12). ⚠ It stays a real hit target rather than shrinking to the 15px glyph:
          // this sits in a row of worded controls and has to be as easy to hit as they are.
          width: 28,
          height: 26,
          borderRadius: radius.lg,
          // `.disclosure` sets `cursor: help`, right for a label behind a hover and wrong for a
          // control that does something — the region pills and the view toggle override it too.
          cursor: ready && !working ? 'pointer' : 'not-allowed',
          fontFamily: font.sans,
          fontSize: fontSize.captionStrong,
          background: color.surfaceTrack,
          color: ready ? color.ink : color.inkHint,
          opacity: working ? 0.7 : 1,
        }}
      >
        {working ? <ProgressRing percent={percent} /> : <SheetIcon />}
      </Disclosure>

      {/*
        ⚠ **Mounted and empty rather than rendered on demand.** A live region inserted at the
        moment it fills is not reliably announced — the same reason the header's status element
        stays in the tree while healthy (FR-015a). It is the ONLY thing a screen-reader user has
        while the ring sweeps silently.
      */}
      <span
        role="status"
        /*
         * ⚠ **One live region, shown only when a sighted reader needs it.** Progress and
         * completion are already on screen — the ring sweeps, then a dialog opens — so
         * announcing them is all that is left to do, and `sr-only` is right. A failure has no
         * other surface at all, so the same element becomes visible rather than a second one
         * appearing beside it: two nodes carrying one message is a message announced twice.
         */
        className={state.phase === 'failed' ? undefined : 'sr-only'}
        style={
          state.phase === 'failed'
            ? { fontFamily: font.sans, fontSize: fontSize.caption, color: color.negative }
            : undefined
        }
      >
        {working && percent != null && state.phase === 'working'
          ? // The figure is spoken with its subject: "63%" alone, announced out of nowhere on a
            // page of numbers, names nothing.
            `Exporting ${state.total.toLocaleString('en-US')} rows — ${percent}% complete`
          : state.phase === 'done'
            ? `Export complete — ${state.rows.toLocaleString('en-US')} rows`
            : state.phase === 'failed'
              ? state.message
              : ''}
      </span>

      {state.phase === 'done' ? (
        <ExportDoneDialog
          rows={state.rows}
          url={state.url}
          region={scope || null}
          tabs={
            delivery ? 'submissions' : allClients ? 'per-account' : 'jobs-and-submissions'
          }
          onClose={() => setState({ phase: 'idle' })}
        />
      ) : null}

    </div>
  );
}
